import { useCallback, useEffect, useRef, useState } from "react";
import {
  MapContainer,
  TileLayer,
  LayersControl,
  LayerGroup,
  Marker,
  Popup,
  useMap,
  useMapEvents,
} from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet-draw/dist/leaflet.draw.css";
import "leaflet-draw";
import SEO from "../components/SEO";



delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
  iconUrl: require("leaflet/dist/images/marker-icon.png"),
  shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
});

const API = "https://gis-api.saudidune.com";

const OSM_ATTR =
  '&copy; <a href="https://openstreetmap.org/copyright">OpenStreetMap</a>';
const ESRI_ATTR =
  "Tiles &copy; Esri &mdash; Source: Esri, Maxar, Earthstar Geographics, and the GIS User Community";

const ESRI_SAT_URL =
  "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}";
const ESRI_LABELS_URL =
  "https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}";
const OSM_URL = "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png";

// ---------------- Draw controls ----------------
function DrawControls({ setResult, setLastGeoJSON }) {
  const map = useMap();

  useEffect(() => {
    const drawnItems = new L.FeatureGroup();
    map.addLayer(drawnItems);

    let elevOverlay = null;

    const drawControl = new L.Control.Draw({
      position: "topright",
      draw: {
        polygon: true,
        rectangle: true,
        polyline: false,
        circle: false,
        marker: false,
        circlemarker: false,
      },
      edit: { featureGroup: drawnItems },
    });
    map.addControl(drawControl);

    async function onCreated(e) {
      drawnItems.clearLayers();
      const layer = e.layer;
      drawnItems.addLayer(layer);
      const geojson = layer.toGeoJSON();
      setLastGeoJSON(geojson);

      try {
        const res = await fetch(`${API}/gis/analyze`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(geojson),
        });
        if (!res.ok) throw new Error(`Backend returned ${res.status}`);
        const data = await res.json();

        if (elevOverlay) map.removeLayer(elevOverlay);
        elevOverlay = L.imageOverlay(data.overlay.image, data.overlay.bounds, {
          opacity: 0.75,
          interactive: false,
        });
        elevOverlay.addTo(map);
        setResult(data.summary);
      } catch (err) {
        console.error("Analyze failed:", err);
      }
    }

    function onDeleted() {
      if (elevOverlay) {
        map.removeLayer(elevOverlay);
        elevOverlay = null;
      }
      setResult(null);
      setLastGeoJSON(null);
    }

    map.on(L.Draw.Event.CREATED, onCreated);
    map.on(L.Draw.Event.DELETED, onDeleted);

    return () => {
      map.off(L.Draw.Event.CREATED, onCreated);
      map.off(L.Draw.Event.DELETED, onDeleted);
      map.removeControl(drawControl);
      map.removeLayer(drawnItems);
      if (elevOverlay) map.removeLayer(elevOverlay);
    };
  }, [map, setResult, setLastGeoJSON]);

  return null;
}

function ClickCatcher({ enabled, onPick }) {
  useMapEvents({
    click(e) {
      if (enabled) onPick(e.latlng);
    },
  });
  return null;
}

function DeepLinkHandler({ places, markerRefs }) {
  const map = useMap();
  const handled = useRef(false);

  useEffect(() => {
    if (handled.current || places.length === 0) return;
    const params = new URLSearchParams(window.location.search);
    const placeId = params.get("place");
    if (!placeId) return;
    const target = places.find((p) => p.id === placeId);
    if (!target) return;

    handled.current = true;
    map.flyTo([target.lat, target.lng], 17, { duration: 1.5 });
    setTimeout(() => {
      const ref = markerRefs.current[placeId];
      if (ref) ref.openPopup();
    }, 1600);
  }, [places, map, markerRefs]);

  return null;
}

function PlacePopup({ place, onDelete }) {
  return (
    <div style={{ textAlign: "center", maxWidth: "240px" }}>
      {place.photo_url && (
        <img
          src={place.photo_url}
          alt={place.name}
          style={{ width: "100%", borderRadius: "8px", marginBottom: "6px" }}
        />
      )}
      <div style={{ fontWeight: "bold", fontSize: "15px" }}>{place.name}</div>
      <div style={{ fontSize: "12px", color: "#777", marginBottom: "8px" }}>
        Owner
      </div>

      {place.whatsapp_url && (
        <a
          href={place.whatsapp_url}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            display: "block",
            padding: "6px 10px",
            marginBottom: "6px",
            borderRadius: "6px",
            background: "#25D366",
            color: "#fff",
            textDecoration: "none",
            fontSize: "13px",
            fontWeight: "bold",
          }}
        >
          💬 WhatsApp
        </a>
      )}

      {place.links &&
        place.links.map((lnk, i) => (
          <a
            key={i}
            href={lnk.url}
            target={lnk.internal ? "_self" : "_blank"}
            rel={lnk.internal ? undefined : "noopener noreferrer"}
            style={{
              display: "block",
              padding: "6px 10px",
              marginBottom: "6px",
              borderRadius: "6px",
              background: "#2c7be5",
              color: "#fff",
              textDecoration: "none",
              fontSize: "13px",
            }}
          >
            🔗 {lnk.label}
          </a>
        ))}

      <button
        onClick={() => onDelete(place.id)}
        style={{
          marginTop: "4px",
          padding: "4px 10px",
          borderRadius: "6px",
          border: "1px solid #e0b4b4",
          background: "#fdecec",
          color: "#c0392b",
          cursor: "pointer",
          fontSize: "12px",
        }}
      >
        Remove
      </button>
    </div>
  );
}

function FactorBar({ label, value }) {
  return (
    <div style={{ marginBottom: "8px" }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          fontSize: "14px",
        }}
      >
        <span>{label}</span>
        <span>{Math.round(value * 100)}%</span>
      </div>
      <div
        style={{ background: "#e5e5e5", borderRadius: "4px", height: "8px" }}
      >
        <div
          style={{
            width: `${Math.round(value * 100)}%`,
            height: "100%",
            borderRadius: "4px",
            background:
              value >= 0.65 ? "#c0392b" : value >= 0.4 ? "#e67e22" : "#27ae60",
          }}
        />
      </div>
    </div>
  );
}

export default function GISPage() {
  const [result, setResult] = useState(null);
  const [lastGeoJSON, setLastGeoJSON] = useState(null);

  // Report form
  const [reportOpen, setReportOpen] = useState(false);
  const [engineer, setEngineer] = useState("");
  const [notes, setNotes] = useState("");
  const [generating, setGenerating] = useState(false);

  // Places
  const [places, setPlaces] = useState([]);
  const [addMode, setAddMode] = useState(false);
  const [pending, setPending] = useState(null);
  const [placeName, setPlaceName] = useState("");
  const [photoFile, setPhotoFile] = useState(null);
  const [whatsapp, setWhatsapp] = useState("");
  const [links, setLinks] = useState([]);
  const [saving, setSaving] = useState(false);

  const markerRefs = useRef({});

  const loadPlaces = useCallback(async () => {
    try {
      const res = await fetch(`${API}/gis/markers`);
      if (res.ok) setPlaces(await res.json());
    } catch (err) {
      console.error("Loading places failed:", err);
    }
  }, []);

  useEffect(() => {
    loadPlaces();
  }, [loadPlaces]);

  function resetForm() {
    setPending(null);
    setPlaceName("");
    setPhotoFile(null);
    setWhatsapp("");
    setLinks([]);
  }

  async function savePlace() {
    if (!pending || !placeName.trim()) return;
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("name", placeName.trim());
      fd.append("lat", pending.lat);
      fd.append("lng", pending.lng);
      if (whatsapp.trim()) fd.append("whatsapp", whatsapp.trim());
      const cleanLinks = links.filter((l) => l.label.trim() && l.url.trim());
      fd.append("links_json", JSON.stringify(cleanLinks));
      if (photoFile) fd.append("photo", photoFile);

      const res = await fetch(`${API}/gis/markers`, {
        method: "POST",
        body: fd,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `Backend returned ${res.status}`);
      }
      resetForm();
      setAddMode(false);
      loadPlaces();
    } catch (err) {
      alert(err.message);
      console.error("Saving place failed:", err);
    } finally {
      setSaving(false);
    }
  }

  async function removePlace(id) {
    try {
      await fetch(`${API}/gis/markers/${id}`, { method: "DELETE" });
      loadPlaces();
    } catch (err) {
      console.error("Deleting place failed:", err);
    }
  }

  function addLinkRow() {
    setLinks([...links, { label: "", url: "" }]);
  }
  function updateLink(i, field, value) {
    setLinks(links.map((l, idx) => (idx === i ? { ...l, [field]: value } : l)));
  }
  function removeLink(i) {
    setLinks(links.filter((_, idx) => idx !== i));
  }

  async function generateReport() {
    if (!lastGeoJSON) return;
    setGenerating(true);
    try {
      const res = await fetch(`${API}/gis/report`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          geometry: lastGeoJSON.geometry,
          engineer: engineer.trim(),
          notes: notes,
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `Server returned ${res.status}`);
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const cd = res.headers.get("Content-Disposition") || "";
      const match = cd.match(/filename="?([^"]+)"?/);
      a.download = match ? match[1] : "reconnaissance_report.pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      setReportOpen(false);
    } catch (err) {
      alert("Report failed: " + err.message);
      console.error(err);
    } finally {
      setGenerating(false);
    }
  }

  return (
    <div style={{ padding: "20px" }}>

      <SEO
        title="GIS Flood Analysis for Saudi Cities — Free Interactive Map — SaudiDune"
        description="Draw any polygon on a Saudi city map and get instant flood risk analysis, slope, drainage and elevation — plus engineer-ready PDF reports in Arabic."
      />
      <h2>GIS Flood Analysis</h2>

      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "12px",
          marginBottom: "10px",
        }}
      >
        <button
          onClick={() => {
            setAddMode(!addMode);
            if (addMode) resetForm();
          }}
          style={{
            padding: "8px 14px",
            borderRadius: "8px",
            border: "1px solid #ccc",
            cursor: "pointer",
            background: addMode ? "#2c7be5" : "#fff",
            color: addMode ? "#fff" : "#333",
            fontWeight: "bold",
          }}
        >
          {addMode ? "Click the map to place the pin..." : "+ Add place"}
        </button>
        <span style={{ color: "#666", fontSize: "14px" }}>
          {addMode
            ? "Click anywhere on the map to place your pin."
            : "Draw a polygon for flood analysis, or add places with photos, WhatsApp, and links."}
        </span>
      </div>

      {pending && (
        <div
          style={{
            marginBottom: "10px",
            padding: "14px",
            background: "#eef4ff",
            border: "1px solid #b9cff5",
            borderRadius: "10px",
          }}
        >
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: "10px",
              alignItems: "center",
              marginBottom: "10px",
            }}
          >
            <strong>New place</strong>
            <span style={{ fontSize: "13px", color: "#555" }}>
              ({pending.lat.toFixed(5)}, {pending.lng.toFixed(5)})
            </span>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: "10px",
              marginBottom: "10px",
            }}
          >
            <input
              placeholder="Owner / place name"
              value={placeName}
              onChange={(e) => setPlaceName(e.target.value)}
              style={{
                padding: "8px 10px",
                borderRadius: "6px",
                border: "1px solid #ccc",
              }}
            />
            <input
              placeholder="WhatsApp e.g. 0501234567 (optional)"
              value={whatsapp}
              onChange={(e) => setWhatsapp(e.target.value)}
              style={{
                padding: "8px 10px",
                borderRadius: "6px",
                border: "1px solid #ccc",
              }}
            />
          </div>

          <div style={{ marginBottom: "10px" }}>
            <label
              style={{ fontSize: "13px", color: "#555", marginRight: "8px" }}
            >
              Photo (optional):
            </label>
            <input
              type="file"
              accept="image/*"
              onChange={(e) => setPhotoFile(e.target.files[0] || null)}
            />
          </div>

          <div style={{ marginBottom: "10px" }}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "6px",
              }}
            >
              <span style={{ fontSize: "13px", color: "#555" }}>
                Links (Store, Website, Instagram...)
              </span>
              <button
                type="button"
                onClick={addLinkRow}
                style={{
                  padding: "4px 10px",
                  borderRadius: "6px",
                  border: "1px solid #2c7be5",
                  background: "#fff",
                  color: "#2c7be5",
                  cursor: "pointer",
                  fontSize: "12px",
                }}
              >
                + Add link
              </button>
            </div>
            {links.map((l, i) => (
              <div
                key={i}
                style={{
                  display: "grid",
                  gridTemplateColumns: "150px 1fr auto",
                  gap: "6px",
                  marginBottom: "6px",
                }}
              >
                <input
                  placeholder="Label e.g. Store"
                  value={l.label}
                  onChange={(e) => updateLink(i, "label", e.target.value)}
                  style={{
                    padding: "6px 8px",
                    borderRadius: "6px",
                    border: "1px solid #ccc",
                  }}
                />
                <input
                  placeholder="https://..."
                  value={l.url}
                  onChange={(e) => updateLink(i, "url", e.target.value)}
                  style={{
                    padding: "6px 8px",
                    borderRadius: "6px",
                    border: "1px solid #ccc",
                  }}
                />
                <button
                  type="button"
                  onClick={() => removeLink(i)}
                  style={{
                    padding: "4px 8px",
                    borderRadius: "6px",
                    border: "1px solid #ccc",
                    background: "#fff",
                    cursor: "pointer",
                  }}
                >
                  ×
                </button>
              </div>
            ))}
          </div>

          <div style={{ display: "flex", gap: "8px" }}>
            <button
              onClick={savePlace}
              disabled={saving || !placeName.trim()}
              style={{
                padding: "8px 16px",
                borderRadius: "6px",
                border: "none",
                background: "#27ae60",
                color: "#fff",
                cursor: "pointer",
                fontWeight: "bold",
              }}
            >
              {saving ? "Saving..." : "Save"}
            </button>
            <button
              onClick={resetForm}
              style={{
                padding: "8px 16px",
                borderRadius: "6px",
                border: "1px solid #ccc",
                background: "#fff",
                cursor: "pointer",
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      <div style={{ height: "75vh", borderRadius: "12px", overflow: "hidden" }}>
        <MapContainer
          center={[21.27, 40.41]}
          zoom={12}
          style={{ height: "100%", width: "100%" }}
        >
          <LayersControl position="topleft">
            <LayersControl.BaseLayer name="Streets">
              <TileLayer url={OSM_URL} attribution={OSM_ATTR} />
            </LayersControl.BaseLayer>
            <LayersControl.BaseLayer checked name="Satellite">
              <TileLayer
                url={ESRI_SAT_URL}
                attribution={ESRI_ATTR}
                maxZoom={19}
              />
            </LayersControl.BaseLayer>
            <LayersControl.BaseLayer name="Hybrid (Satellite + Labels)">
              <LayerGroup>
                <TileLayer
                  url={ESRI_SAT_URL}
                  attribution={ESRI_ATTR}
                  maxZoom={19}
                />
                <TileLayer url={ESRI_LABELS_URL} maxZoom={19} />
              </LayerGroup>
            </LayersControl.BaseLayer>
          </LayersControl>

          <DrawControls setResult={setResult} setLastGeoJSON={setLastGeoJSON} />
          <ClickCatcher enabled={addMode && !pending} onPick={setPending} />
          <DeepLinkHandler places={places} markerRefs={markerRefs} />

          {pending && <Marker position={[pending.lat, pending.lng]} />}

          {places.map((p) => (
            <Marker
              key={p.id}
              position={[p.lat, p.lng]}
              ref={(ref) => {
                if (ref) markerRefs.current[p.id] = ref;
              }}
            >
              <Popup>
                <PlacePopup place={p} onDelete={removePlace} />
              </Popup>
            </Marker>
          ))}
        </MapContainer>
      </div>

      {result && (
        <div
          style={{
            marginTop: "20px",
            padding: "15px",
            background: "#f7f7f7",
            borderRadius: "10px",
            border: "1px solid #ddd",
          }}
        >
          <h3>
            Flood Analysis Result{" — "}
            <span
              style={{
                color:
                  result.risk_score >= 0.65
                    ? "#c0392b"
                    : result.risk_score >= 0.4
                      ? "#e67e22"
                      : "#27ae60",
              }}
            >
              {result.comments} ({result.risk_score})
            </span>
          </h3>

          <p>
            <strong>Elevation:</strong> {result.min_elevation} –{" "}
            {result.max_elevation} m (range {result.elevation_range} m)
          </p>
          <p>
            <strong>Mean slope:</strong> {result.mean_slope_deg}°
          </p>
          <p>
            <strong>Largest drainage channel:</strong>{" "}
            {result.max_flow_accumulation.toLocaleString()} upstream cells
          </p>

          {result.factors && (
            <div style={{ maxWidth: "420px", marginTop: "12px" }}>
              <FactorBar
                label="Drainage (channel passes through)"
                value={result.factors.drainage}
              />
              <FactorBar
                label="Flatness (water ponds)"
                value={result.factors.flatness}
              />
              <FactorBar
                label="Low-lying (relative to region)"
                value={result.factors.lowness}
              />
            </div>
          )}

          {/* Export PDF report button */}
          {lastGeoJSON && !reportOpen && (
            <button
              onClick={() => setReportOpen(true)}
              style={{
                marginTop: "16px",
                padding: "10px 18px",
                borderRadius: "8px",
                border: "none",
                background: "#1a365d",
                color: "#fff",
                cursor: "pointer",
                fontWeight: "bold",
                fontSize: "14px",
              }}
            >
              📄 تصدير تقرير الرفع المساحي (PDF)
            </button>
          )}

          {reportOpen && (
            <div
              style={{
                marginTop: "16px",
                padding: "14px",
                background: "#f0f4fa",
                border: "1px solid #b9cff5",
                borderRadius: "10px",
                direction: "rtl",
                textAlign: "right",
              }}
            >
              <h4 style={{ marginTop: 0, color: "#1a365d" }}>
                معلومات التقرير
              </h4>
              <div style={{ marginBottom: "10px" }}>
                <label
                  style={{
                    display: "block",
                    fontSize: "13px",
                    marginBottom: "4px",
                    color: "#555",
                  }}
                >
                  اسم المهندس المسؤول:
                </label>
                <input
                  value={engineer}
                  onChange={(e) => setEngineer(e.target.value)}
                  placeholder="مثال: م. عبدالله محمد"
                  style={{
                    width: "100%",
                    padding: "8px 10px",
                    borderRadius: "6px",
                    border: "1px solid #ccc",
                    direction: "rtl",
                    fontSize: "14px",
                  }}
                />
              </div>
              <div style={{ marginBottom: "12px" }}>
                <label
                  style={{
                    display: "block",
                    fontSize: "13px",
                    marginBottom: "4px",
                    color: "#555",
                  }}
                >
                  ملاحظات المهندس (تظهر في التقرير):
                </label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="ملاحظات ميدانية، توصيات، معلومات إضافية عن الموقع..."
                  rows={4}
                  style={{
                    width: "100%",
                    padding: "8px 10px",
                    borderRadius: "6px",
                    border: "1px solid #ccc",
                    direction: "rtl",
                    fontSize: "14px",
                    fontFamily: "inherit",
                    resize: "vertical",
                  }}
                />
              </div>
              <div style={{ display: "flex", gap: "8px" }}>
                <button
                  onClick={generateReport}
                  disabled={generating}
                  style={{
                    padding: "10px 18px",
                    borderRadius: "6px",
                    border: "none",
                    background: "#27ae60",
                    color: "#fff",
                    cursor: "pointer",
                    fontWeight: "bold",
                  }}
                >
                  {generating ? "جاري توليد التقرير..." : "توليد التقرير"}
                </button>
                <button
                  onClick={() => setReportOpen(false)}
                  disabled={generating}
                  style={{
                    padding: "10px 18px",
                    borderRadius: "6px",
                    border: "1px solid #ccc",
                    background: "#fff",
                    cursor: "pointer",
                  }}
                >
                  إلغاء
                </button>
              </div>
              {generating && (
                <p
                  style={{ marginTop: "8px", fontSize: "12px", color: "#666" }}
                >
                  قد يستغرق التوليد ٥-١٠ ثواني (تحميل خريطة، جلب الطرق، بناء
                  PDF).
                </p>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
